import React, { useMemo } from "react";
import { Formik, ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  extractSelectedSchemes,
  getCodeByDescription,
} from "../../../utils/oqicHelpers";
import { colors } from "../../../config/oqic";
import { getQuote } from "../../../features/product";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Loader } from "../../common/loader";

const OquicMotorPersonalDetailForm = () => {
  const { t } = useTranslation(); // ✅ Add translation hook

  // ✅ Create validation schema with translated messages
  const validationSchema = Yup.object({
    civilId: Yup.string()
      // .matches(/^\d{8}$/, t("oquicMotor.personalDetail.validationMessages.civilIdFormat"))
      .required(t("oquicMotor.personalDetail.validationMessages.civilIdRequired")),

    mobileNo: Yup.string()
      .matches(/^\d{8}$/, t("oquicMotor.personalDetail.validationMessages.mobileNoFormat"))
      .required(t("oquicMotor.personalDetail.validationMessages.mobileNoRequired")),

    emailId: Yup.string()
      .email(t("oquicMotor.personalDetail.validationMessages.emailFormat"))
      .required(t("oquicMotor.personalDetail.validationMessages.emailRequired")),

    address: Yup.string()
      .min(2, t("oquicMotor.personalDetail.validationMessages.addressMin"))
      .required(t("oquicMotor.personalDetail.validationMessages.addressRequired")),

    remark: Yup.string().max(255, t("oquicMotor.personalDetail.validationMessages.remarkMax")),
  });

  // ✅ Create initial values
  const initialValues = {
    civilId: "",
    mobileNo: "",
    emailId: "",
    address: "",
    remark: "",
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const quoteLoader = useSelector((state) => state.product.quoteLoader);
  const planData = useMemo(
    () => location?.state?.data || {},
    [location?.state]
  );

  if (quoteLoader || !planData || !planData?.extracted_data) {
    return <Loader type="logo-gif" />;
  }

  return (
    <div className="container my-5">
      <div className="card shadow-sm">
        <div className="card-header bg-dark text-white">
          <h4 className="mb-0">
            {t("oquicMotor.personalDetail.title")} {/* ✅ Translated title */}
          </h4>
        </div>
        <div className="card-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              const { extracted_data } = planData;

              const apiRequestData = {
                planData,
                ...extracted_data,
                ...values,
                insurance_company_product_mapping_id:
                  planData?.insurance_company_product_mapping_id,
                company_id: planData?.company_id,
                quoteNo: extracted_data?.quote_id,
                chassisNo: extracted_data?.chassisNo,
                policyFromDate: extracted_data?.policyFromDate,
                regnLocation: "011",
                registrationNo: extracted_data?.plate_no,
                plateCharacter: extracted_data?.plate_char,
                tcfNo: "",
                weightCode: extracted_data?.oquicVehicleType,
                engineNo: extracted_data?.vehicle_engine_no,
                colorCode: getCodeByDescription(
                  extracted_data?.vehicle_color,
                  colors
                ),
                financedStatus: "0",
                financeBank: "",
                vatRefNo: "",
                geoArea: extracted_data?.geoArea,
                orangeCardNo: "",
                aaaCardNo: "",
                cardRefNo: "",
                schemes: extractSelectedSchemes(planData),
              };

              dispatch(getQuote(apiRequestData)).then((res) => {
                navigate("/checkout", { state: { existingData: res.payload } });
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Civil ID */}
                  <div className="col-md-4">
                    <label htmlFor="civilId" className="form-label">
                      {t("oquicMotor.personalDetail.formLabels.civilId")}
                    </label>
                    <Form.Control
                      name="civilId"
                      type="text"
                      value={values.civilId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.civilId && errors.civilId ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="civilId"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  {/* Mobile No */}
                  <div className="col-md-4">
                    <label htmlFor="mobileNo" className="form-label">
                      {t("oquicMotor.personalDetail.formLabels.mobileNo")}
                    </label>
                    <Form.Control
                      name="mobileNo"
                      type="text"
                      value={values.mobileNo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.mobileNo && errors.mobileNo ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="mobileNo"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  {/* Email */}
                  <div className="col-md-4">
                    <label htmlFor="emailId" className="form-label">
                      {t("oquicMotor.personalDetail.formLabels.emailId")}
                    </label>
                    <Form.Control
                      name="emailId"
                      type="email"
                      value={values.emailId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.emailId && errors.emailId ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="emailId"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  {/* Address */}
                  <div className="col-md-12">
                    <label htmlFor="address" className="form-label">
                      {t("oquicMotor.personalDetail.formLabels.address")}
                    </label>
                    <Form.Control
                      name="address"
                      type="text"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.address && errors.address ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="address"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  {/* Remark */}
                  <div className="col-md-12">
                    <label htmlFor="remark" className="form-label">
                      {t("oquicMotor.personalDetail.formLabels.remark")}
                    </label>
                    <Form.Control
                      name="remark"
                      as="textarea"
                      value={values.remark}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.remark && errors.remark ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="remark"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </div>

                <div className="form-submitbtn mt-2">
                  <Button type="submit" className="mt-2 h-btn">
                    {t("oquicMotor.personalDetail.buttons.submit")}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default OquicMotorPersonalDetailForm;